import React, { useEffect, useMemo, useState } from "react"

import {
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns"
import { ArrowDown, ArrowUp, Calendar, Eye, TrendingUp, Users } from "lucide-react"
import { AlertCircle } from "lucide-react"
import {
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts"

import DiaryEntryCard from "@/components/people/diary/DiaryEntryCard"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Calendar as CalendarComponent } from "@/components/ui/calendar"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useDiaryEntrySubmissionSummary } from "@/hooks/useDiaryEntrySubmissionSummary"
import { IndividualContributor } from "@/types/person"

type SortField = "author" | "totalEntries"
type SortDirection = "asc" | "desc"

const UsersSubmittingDiaryEntriesTab: React.FC = () => {
  // Date range state (default: last 30 days)
  const [startDate, setStartDate] = useState<Date>(() => {
    return subDays(new Date(), 30)
  })
  const [endDate, setEndDate] = useState<Date>(() => {
    return new Date()
  })
  const [dateRangePreset, setDateRangePreset] = useState<
    "thisWeek" | "lastWeek" | "thisMonth" | "lastMonth" | "last30Days" | "custom"
  >("last30Days")

  // Sorting state
  const [sortField, setSortField] = useState<SortField>("totalEntries")
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc")

  // Selected contributor for drill-down
  const [selectedContributor, setSelectedContributor] = useState<IndividualContributor | null>(null)

  // Date range preset handlers
  const handleDatePreset = (
    preset: "thisWeek" | "lastWeek" | "thisMonth" | "lastMonth" | "last30Days" | "custom",
  ) => {
    setDateRangePreset(preset)
    const now = new Date()

    switch (preset) {
      case "thisWeek":
        setStartDate(startOfWeek(now, { weekStartsOn: 1 }))
        setEndDate(endOfWeek(now, { weekStartsOn: 1 }))
        break
      case "lastWeek": {
        const lastWeekStart = subWeeks(now, 1)
        setStartDate(startOfWeek(lastWeekStart, { weekStartsOn: 1 }))
        setEndDate(endOfWeek(lastWeekStart, { weekStartsOn: 1 }))
        break
      }
      case "thisMonth":
        setStartDate(startOfMonth(now))
        setEndDate(endOfMonth(now))
        break
      case "lastMonth": {
        const lastMonth = subMonths(now, 1)
        setStartDate(startOfMonth(lastMonth))
        setEndDate(endOfMonth(lastMonth))
        break
      }
      case "last30Days":
        setStartDate(subDays(now, 30))
        setEndDate(now)
        break
      case "custom":
        // Keep current dates, just mark as custom
        break
    }
  }

  // Update preset when dates change manually
  useEffect(() => {
    if (dateRangePreset !== "custom") {
      const now = new Date()
      const thisWeekStart = startOfWeek(now, { weekStartsOn: 1 })
      const thisWeekEnd = endOfWeek(now, { weekStartsOn: 1 })
      const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 })
      const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 })
      const thisMonthStart = startOfMonth(now)
      const thisMonthEnd = endOfMonth(now)
      const lastMonthStart = startOfMonth(subMonths(now, 1))
      const lastMonthEnd = endOfMonth(subMonths(now, 1))
      const last30DaysStart = subDays(now, 30)
      const last30DaysEnd = now

      if (
        startDate.getTime() === thisWeekStart.getTime() &&
        endDate.getTime() === thisWeekEnd.getTime()
      ) {
        setDateRangePreset("thisWeek")
      } else if (
        startDate.getTime() === lastWeekStart.getTime() &&
        endDate.getTime() === lastWeekEnd.getTime()
      ) {
        setDateRangePreset("lastWeek")
      } else if (
        startDate.getTime() === thisMonthStart.getTime() &&
        endDate.getTime() === thisMonthEnd.getTime()
      ) {
        setDateRangePreset("thisMonth")
      } else if (
        startDate.getTime() === lastMonthStart.getTime() &&
        endDate.getTime() === lastMonthEnd.getTime()
      ) {
        setDateRangePreset("lastMonth")
      } else if (
        Math.abs(startDate.getTime() - last30DaysStart.getTime()) < 86400000 && // Within 1 day tolerance
        Math.abs(endDate.getTime() - last30DaysEnd.getTime()) < 86400000
      ) {
        setDateRangePreset("last30Days")
      } else {
        setDateRangePreset("custom")
      }
    }
  }, [startDate, endDate, dateRangePreset])

  // Fetch data
  const { data, isLoading, error } = useDiaryEntrySubmissionSummary({
    startDate: format(startDate, "yyyy-MM-dd"),
    endDate: format(endDate, "yyyy-MM-dd"),
  })

  // Sort contributors
  const sortedContributors = useMemo(() => {
    if (!data?.individualContributors) return []

    const sorted = [...data.individualContributors]
    sorted.sort((a, b) => {
      let comparison = 0
      if (sortField === "author") {
        comparison = a.author.localeCompare(b.author)
      } else if (sortField === "totalEntries") {
        comparison = a.totalEntries - b.totalEntries
      }
      return sortDirection === "asc" ? comparison : -comparison
    })
    return sorted
  }, [data?.individualContributors, sortField, sortDirection])

  // Sort sub-team summary
  const sortedSubTeams = useMemo(() => {
    if (!data?.subTeamSummary) return []
    return Object.entries(data.subTeamSummary)
      .sort(([, a], [, b]) => b - a)
      .map(([team, count]) => ({ team, count }))
  }, [data?.subTeamSummary])

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("desc")
    }
  }

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null
    return sortDirection === "asc" ? (
      <ArrowUp className="h-4 w-4 ml-1" />
    ) : (
      <ArrowDown className="h-4 w-4 ml-1" />
    )
  }

  // Mini sparkline component
  const MiniTrendline: React.FC<{ data: Array<{ date: string; count: number }> }> = ({ data }) => {
    if (!data || data.length === 0) {
      return <span className="text-palette-jungleGreen400 text-xs">No data</span>
    }

    const maxValue = Math.max(...data.map((d) => d.count), 1)
    const width = 100
    const height = 30

    return (
      <div className="w-full h-8">
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={data} width={width} height={height}>
            <Line
              type="monotone"
              dataKey="count"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <XAxis hide dataKey="date" />
            <YAxis hide domain={[0, maxValue]} />
            <RechartsTooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white border rounded p-2 shadow-lg text-xs">
                      <p className="font-semibold">{payload[0].payload.date}</p>
                      <p className="text-blue-600">{payload[0].value} entries</p>
                    </div>
                  )
                }
                return null
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header with Date Range Picker */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Users Submitting Diary Entries</h2>
          <p className="text-sm text-palette-jungleGreen500 mt-1">
            Monitor who is submitting diary entries and what information they are logging
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Date Range Preset Buttons */}
          <div className="flex items-center gap-1 border rounded-md p-1">
            <Button
              variant={dateRangePreset === "thisWeek" ? "default" : "ghost"}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => handleDatePreset("thisWeek")}
            >
              This Week
            </Button>
            <Button
              variant={dateRangePreset === "lastWeek" ? "default" : "ghost"}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => handleDatePreset("lastWeek")}
            >
              Last Week
            </Button>
            <Button
              variant={dateRangePreset === "thisMonth" ? "default" : "ghost"}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => handleDatePreset("thisMonth")}
            >
              This Month
            </Button>
            <Button
              variant={dateRangePreset === "lastMonth" ? "default" : "ghost"}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => handleDatePreset("lastMonth")}
            >
              Last Month
            </Button>
            <Button
              variant={dateRangePreset === "last30Days" ? "default" : "ghost"}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => handleDatePreset("last30Days")}
            >
              Last 30 Days
            </Button>
            <Button
              variant={dateRangePreset === "custom" ? "default" : "ghost"}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setDateRangePreset("custom")}
            >
              Custom
            </Button>
          </div>

          {/* Date Picker */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Calendar className="h-4 w-4" />
                {format(startDate, "MMM dd")} - {format(endDate, "MMM dd")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <div className="p-4">
                <div className="flex gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Start Date</label>
                    <CalendarComponent
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => {
                        if (date) {
                          setStartDate(date)
                          setDateRangePreset("custom")
                        }
                      }}
                      initialFocus
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">End Date</label>
                    <CalendarComponent
                      mode="single"
                      selected={endDate}
                      onSelect={(date) => {
                        if (date) {
                          setEndDate(date)
                          setDateRangePreset("custom")
                        }
                      }}
                      initialFocus
                    />
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error loading submission summary:{" "}
            {error instanceof Error ? error.message : "Unknown error"}
          </AlertDescription>
        </Alert>
      )}

      {/* Sub Team Summary Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Sub Team Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : sortedSubTeams.length === 0 ? (
            <p className="text-palette-jungleGreen500 text-center py-8">No team data available</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedSubTeams.map(({ team, count }) => (
                <div
                  key={team}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-palette-jungleGreen000 transition-colors"
                >
                  <div>
                    <p className="font-semibold text-palette-jungleGreen900">{team}</p>
                    <p className="text-sm text-palette-jungleGreen500">Total entries</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-600">{count}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Individual Contributor List Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Individual Contributor List
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : sortedContributors.length === 0 ? (
            <p className="text-palette-jungleGreen500 text-center py-8">No contributors found</p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="cursor-pointer" onClick={() => toggleSort("author")}>
                      <div className="flex items-center">Author {renderSortIcon("author")}</div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => toggleSort("totalEntries")}
                    >
                      <div className="flex items-center">
                        Total Entries {renderSortIcon("totalEntries")}
                      </div>
                    </TableHead>
                    <TableHead>Tag Breakdown</TableHead>
                    <TableHead>Subtag Breakdown</TableHead>
                    <TableHead>Trend</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedContributors.map((contributor) => (
                    <TableRow key={`${contributor.author}::${contributor.authorEmail}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{contributor.author || "Unknown"}</p>
                          {contributor.authorEmail && (
                            <p className="text-sm text-palette-jungleGreen500">
                              {contributor.authorEmail}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-blue-600">
                          {contributor.totalEntries}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {Object.entries(contributor.tagBreakdown)
                            .sort(([, a], [, b]) => b - a)
                            .slice(0, 3)
                            .map(([tag, count]) => (
                              <Badge key={tag} variant="outline" className="text-xs">
                                {tag} ({count})
                              </Badge>
                            ))}
                          {Object.keys(contributor.tagBreakdown).length > 3 && (
                            <Badge variant="secondary" className="text-xs">
                              +{Object.keys(contributor.tagBreakdown).length - 3} more
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {Object.entries(contributor.subtagBreakdown)
                            .sort(([, a], [, b]) => b - a)
                            .slice(0, 3)
                            .map(([subtag, count]) => (
                              <Badge key={subtag} variant="secondary" className="text-xs">
                                {subtag} ({count})
                              </Badge>
                            ))}
                          {Object.keys(contributor.subtagBreakdown).length > 3 && (
                            <Badge variant="secondary" className="text-xs">
                              +{Object.keys(contributor.subtagBreakdown).length - 3} more
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <MiniTrendline data={contributor.timeSeries} />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedContributor(contributor)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Entries
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drill-down Sheet */}
      <Sheet
        open={!!selectedContributor}
        onOpenChange={(open) => !open && setSelectedContributor(null)}
      >
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Diary Entries by {selectedContributor?.author || "Unknown"}</SheetTitle>
            <SheetDescription>
              {selectedContributor?.authorEmail && (
                <p className="text-sm text-palette-jungleGreen500">
                  {selectedContributor.authorEmail}
                </p>
              )}
              <p className="mt-2">Total: {selectedContributor?.totalEntries || 0} entries</p>
            </SheetDescription>
          </SheetHeader>
          <ScrollArea className="mt-6 h-[calc(100vh-120px)]">
            <div className="space-y-4 pr-4">
              {selectedContributor?.entries.map((entry) => (
                <DiaryEntryCard key={entry.id} entry={entry} />
              ))}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  )
}

export default UsersSubmittingDiaryEntriesTab
